import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT, ELLIPSIS,
} from '../../styles/recruiterTheme';
import {
  Button, Typography, Box, Grid, Avatar, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, InputAdornment, Tooltip,
} from '@mui/material';
import styled from 'styled-components';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  ArrowUpward as ArrowUpwardIcon,
  FiberManualRecord as FiberManualRecordIcon,
  EmojiEvents as EmojiEventsIcon,
  Timeline as TimelineIcon,
  PersonSearch as PersonSearchIcon,
  WorkOutline as WorkOutlineIcon,
  Storefront as StorefrontIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// ─── Typography helpers (no DOM prop leaks) ───────────────────────────────────
const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin: 4px 0 0 !important;
  }
`;

const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 20px !important;
    white-space: nowrap;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important;
    }
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const CLIENT_PALETTES = [
  ["#1e3a5f","#3b82f6"],["#1a3a2a","#10b981"],["#3b1f5e","#8b5cf6"],
  ["#5e1f1f","#ef4444"],["#1f3d5e","#0ea5e9"],["#2d3748","#718096"],
  ["#5e3a1f","#f97316"],["#1f4e3d","#34d399"],
];
const clientPalette = (name) =>
  CLIENT_PALETTES[(String(name || "").charCodeAt(0) || 0) % CLIENT_PALETTES.length];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;
const isValidUrl = (v) => {
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    return !!u.hostname && u.hostname.includes(".");
  } catch {
    return false;
  }
};

const validateClient = (f) => {
  if (!String(f.client_id || "").trim())       return "Client ID is required.";
  if (!String(f.company_name || "").trim())    return "Company name is required.";
  if (!f.industry)                             return "Industry is required.";
  if (!String(f.company_size || "").trim())    return "Company size is required.";
  if (!String(f.primary_contact || "").trim()) return "Primary contact is required.";
  if (!String(f.contact_title || "").trim())   return "Contact title is required.";
  if (!EMAIL_RE.test(String(f.email || "").trim())) return "Enter a valid email address.";
  if (!PHONE_RE.test(String(f.phone || "").trim()) || String(f.phone).replace(/\D/g, "").length < 7)
    return "Enter a valid phone number (7–15 digits).";
  if (String(f.website || "").trim() && !isValidUrl(String(f.website).trim()))
    return "Enter a valid website URL.";
  if (f.billing_rate !== "" && f.billing_rate != null) {
    const r = Number(f.billing_rate);
    if (Number.isNaN(r) || r < 0 || r > 100) return "Billing rate must be between 0 and 100.";
  }
  if (f.agreement_start && f.agreement_end && String(f.agreement_end).split("T")[0] < String(f.agreement_start).split("T")[0])
    return "Contract end date cannot be before the start date.";
  return "";
};

const STATUS_META = {
  Active:    { color: "#1B6B3A", bg: "#D4FAE0", dot: "#2E9B54" },
  Inactive:  { color: "#5C4A8A", bg: "#F0EAF9", dot: "#9B8DC4" },
  "On Hold": { color: "#7A5200", bg: "#FFF3CD", dot: "#F59E0B" },
  Prospect:  { color: "#1E3A8A", bg: "#DBEAFE", dot: "#3B82F6" },
};

const INDUSTRIES    = ["Information Technology","Banking & Finance","Healthcare","Manufacturing","Retail","Telecom","Consulting","E-commerce","Automotive","Energy","Other"];
const STATUSES      = ["Active","Inactive","On Hold","Prospect"];
const PAYMENT_TERMS = ["Net 15","Net 30","Net 45","Net 60"];

const EMPTY_FORM = {
  client_id: "", company_name: "", industry: "", company_size: "", location: "",
  primary_contact: "", contact_title: "", email: "", phone: "", city: "", state: "",
  country: "India", address: "", website: "", agreement_type: "", payment_terms: "Net 30",
  agreement_start: "", agreement_end: "", relationship_status: "Active",
  account_manager: "", billing_rate: "", notes: "",
};

// ─── API helpers ──────────────────────────────────────────────────────────────
const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});



const safeJson = async (res) => {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Server error (${res.status}): response was not JSON`);
  }
  if (!res.ok) {
    throw new Error(data?.message || data?.error || data?.detail || `Request failed (${res.status})`);
  }
  return data;
};

const apiFetch = async (url, opts = {}) => {
  if (!url || /^undefined\//.test(url)) throw new Error("API URL is not configured. Check your .env file.");
  return fetch(url, {
    headers: getHeaders(),
    ...opts,
  }).then(safeJson);
};

// ─── Shared style tokens ──────────────────────────────────────────────────────
// 40px outlined inputs/selects (single-line only; multiline keeps its own height).
const FIELD_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.body, bgcolor: "#fff" },
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": { height: CONTROL_HEIGHT.lg },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.border },
  "& .MuiInputLabel-root": { fontFamily: FONT_BODY, fontSize: TYPE.body },
};
const OVERLINE_SX = { fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.25 };
const NUM_SX = { fontFamily: FONT_BODY, fontWeight: 800, fontVariantNumeric: "tabular-nums", lineHeight: 1 };
const BTN_SX = { fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, textTransform: "none", borderRadius: `${RADIUS.sm}px`, whiteSpace: "nowrap" };

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, icon, accent, trend }) {
  return (
    <Box sx={{
      px: 3, py: 2, minHeight: 96, height: "100%", boxSizing: "border-box",
      borderRadius: `${RADIUS.md}px`, bgcolor: "#fff", border: `1px solid ${COLORS.border}`,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
      position: "relative", overflow: "hidden",
      boxShadow: SHADOW.card,
      "&::before": {
        content: '""', position: "absolute", top: 0, left: 0, right: 0,
        height: 3, bgcolor: accent,
      },
    }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ ...NUM_SX, fontSize: TYPE.stat, color: COLORS.primaryDeep }}>
            {value}
          </Typography>
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, px: 1, height: 24, bgcolor: "#D4FAE0", borderRadius: `${RADIUS.pill}px`, flexShrink: 0 }}>
              <ArrowUpwardIcon sx={{ fontSize: 12, color: "#1B6B3A" }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: "#1B6B3A", fontVariantNumeric: "tabular-nums" }}>{trend}</Typography>
            </Box>
          )}
        </Box>
        <Typography title={label} sx={{ ...OVERLINE_SX, ...ELLIPSIS, color: COLORS.muted }}>
          {label}
        </Typography>
        {sub && (
          <Typography title={sub} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.faint, lineHeight: 1.25, ...ELLIPSIS }}>
            {sub}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: 40, height: 40, bgcolor: `${accent}18`, borderRadius: `${RADIUS.sm}px`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </Box>
    </Box>
  );
}

// ─── Client Card ──────────────────────────────────────────────────────────────
function ClientCard({ client, jobCnt, candidateCnt, placedCnt, isSelected, onSelect, onEdit, onDelete, onViewJobs, onViewCandidates }) {
  const [bg, accent] = clientPalette(client.company_name);
  const sm = STATUS_META[client.relationship_status] || STATUS_META["Inactive"];
  const contactLine = client.primary_contact ? `${client.primary_contact}${client.contact_title ? ` · ${client.contact_title}` : ""}` : "";
  const locationLine = [client.city, client.state].filter(Boolean).join(", ");
  const metaSx = { fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, lineHeight: 1.5, ...ELLIPSIS };

  return (
    <Box sx={{
      width: "100%", height: "100%", display: "flex", flexDirection: "column",
      bgcolor: "#fff",
      border: isSelected ? `2px solid ${accent}` : `1px solid ${COLORS.border}`,
      borderRadius: `${RADIUS.md}px`, overflow: "hidden", cursor: "pointer",
      transition: "all 0.15s",
      boxShadow: isSelected ? `0 4px 20px ${accent}22` : SHADOW.card,
      "&:hover": { borderColor: accent, boxShadow: `0 6px 20px ${accent}22`, transform: "translateY(-2px)" },
    }}>
      <Box sx={{ height: 4, flexShrink: 0, background: `linear-gradient(90deg, ${bg}, ${accent})` }} />

      <Box onClick={() => onSelect(client)} sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Header */}
        <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
          <Avatar sx={{ width: 44, height: 44, borderRadius: `${RADIUS.sm}px`, background: `linear-gradient(135deg, ${bg}, ${accent})`, fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
            {client.company_name?.[0]?.toUpperCase()}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography title={client.company_name} sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, lineHeight: 1.4, color: COLORS.primaryDeep, ...ELLIPSIS }}>
              {client.company_name}
            </Typography>
            <Typography title={client.industry} sx={{ ...metaSx, color: COLORS.muted }}>
              {client.industry}
            </Typography>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, px: 1, height: 24, bgcolor: sm.bg, borderRadius: `${RADIUS.pill}px` }}>
              <FiberManualRecordIcon sx={{ fontSize: 8, color: sm.dot }} />
              <Typography sx={{ fontSize: TYPE.overline, fontWeight: 700, color: sm.color, fontFamily: FONT_BODY, whiteSpace: "nowrap" }}>
                {client.relationship_status}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ minHeight: 40, mb: 2 }}>
          {contactLine && (
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <PeopleIcon sx={{ fontSize: 14, color: COLORS.faint, flexShrink: 0 }} />
              <Typography title={contactLine} sx={metaSx}>{contactLine}</Typography>
            </Box>
          )}
          {locationLine && (
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <LocationOnIcon sx={{ fontSize: 14, color: COLORS.faint, flexShrink: 0 }} />
              <Typography title={locationLine} sx={metaSx}>{locationLine}</Typography>
            </Box>
          )}
        </Box>

        {/* Stats row */}
        <Box sx={{ mt: "auto", display: "flex", borderRadius: `${RADIUS.sm}px`, overflow: "hidden", border: "1px solid #F0EAF9" }}>
          {[
            { n: jobCnt,       l: "Jobs",       color: "#4F378B" },
            { n: candidateCnt, l: "Candidates", color: "#0ea5e9" },
            { n: placedCnt,    l: "Placed",     color: "#1B6B3A" },
          ].map(({ n, l, color }, i) => (
            <Box key={l} flex={1} minWidth={0} textAlign="center" py={1.5}
              sx={{ borderRight: i < 2 ? "1px solid #F0EAF9" : "none", bgcolor: i === 2 && placedCnt > 0 ? "#F0FDF4" : "transparent" }}>
              <Typography sx={{ ...NUM_SX, fontSize: TYPE.section, color }}>{n}</Typography>
              <Typography sx={{ ...OVERLINE_SX, ...ELLIPSIS, color: COLORS.faint, mt: 0.5 }}>{l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5, flexShrink: 0, borderTop: "1px solid #F0EAF9", bgcolor: "#FDFBFF" }}>
        <Button size="small" onClick={(e) => { e.stopPropagation(); onViewJobs(client); }}
          sx={{ ...BTN_SX, flex: 1, minWidth: 0, height: CONTROL_HEIGHT.sm, py: 0,
            color: "#4F378B", border: "1px solid rgba(79,55,139,0.25)",
            "&:hover": { bgcolor: "#F3EEF9", borderColor: "#4F378B" } }}>
          View Jobs
        </Button>
        <Button size="small" onClick={(e) => { e.stopPropagation(); onViewCandidates(client); }}
          sx={{ ...BTN_SX, flex: 1, minWidth: 0, height: CONTROL_HEIGHT.sm, py: 0,
            color: "#1B6B3A", border: "1px solid rgba(27,107,58,0.25)",
            "&:hover": { bgcolor: "#D4FAE0", borderColor: "#1B6B3A" } }}>
          Candidates
        </Button>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(client); }}
            sx={{ width: CONTROL_HEIGHT.sm, height: CONTROL_HEIGHT.sm, flexShrink: 0, border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.sm}px`, color: "#6750A4", "&:hover": { bgcolor: "#F3EEF9" } }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(client); }}
            sx={{ width: CONTROL_HEIGHT.sm, height: CONTROL_HEIGHT.sm, flexShrink: 0, border: "1px solid #FFE4E6", borderRadius: `${RADIUS.sm}px`, color: "#C2185B", "&:hover": { bgcolor: "#FFE4E6" } }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function DetailPanel({ client, jobCnt, candidateCnt, placedCnt, onClose, onEdit, onViewJobs, onViewCandidates }) {
  const [bg, accent] = clientPalette(client.company_name);

  const InfoRow = ({ icon, label, value }) => value ? (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={1}
      sx={{ borderBottom: "1px solid #F0EAF9", "&:last-child": { borderBottom: "none" } }}>
      <Box sx={{ color: "#B0A0D0", mt: 0.1, flexShrink: 0 }}>{icon}</Box>
      <Box minWidth={0}>
        <Typography sx={{ ...OVERLINE_SX, color: COLORS.faint }}>{label}</Typography>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.primaryDeep, fontWeight: 500, overflowWrap: "anywhere" }}>{value}</Typography>
      </Box>
    </Box>
  ) : null;

  return (
    <Box sx={{ bgcolor: "#fff", border: "1px solid #EDE7F6", borderRadius: `${RADIUS.lg}px`, overflow: "hidden", position: "sticky", top: 16, boxShadow: SHADOW.panel }}>
      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${bg} 0%, ${accent} 100%)`, p: 3, position: "relative" }}>
        <IconButton size="small" onClick={onClose}
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "#fff" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Avatar sx={{ width: 50, height: 50, bgcolor: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", fontFamily: FONT_BODY, fontSize: TYPE.section, fontWeight: 800, color: "#fff", borderRadius: `${RADIUS.sm}px`, flexShrink: 0 }}>
            {client.company_name?.[0]?.toUpperCase()}
          </Avatar>
          <Box minWidth={0} pr={3}>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, lineHeight: 1.4, color: "#fff", overflowWrap: "anywhere" }}>{client.company_name}</Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: "rgba(255,255,255,0.65)" }}>
              {client.industry} · {client.client_id}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          {[{ n: jobCnt, l: "Jobs" }, { n: candidateCnt, l: "Candidates" }, { n: placedCnt, l: "Placed", highlight: true }].map(({ n, l, highlight }) => (
            <Box key={l} flex={1} minWidth={0} sx={{ bgcolor: highlight && n > 0 ? "rgba(46,155,84,0.25)" : "rgba(255,255,255,0.12)", borderRadius: `${RADIUS.sm}px`, p: 1.5, textAlign: "center" }}>
              <Typography sx={{ ...NUM_SX, fontSize: TYPE.section, color: "#fff" }}>{n}</Typography>
              <Typography sx={{ ...OVERLINE_SX, ...ELLIPSIS, color: "rgba(255,255,255,0.65)", mt: 0.5 }}>{l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        <Typography sx={{ ...OVERLINE_SX, color: COLORS.muted, mb: 1 }}>Contact</Typography>
        <InfoRow icon={<PeopleIcon sx={{ fontSize: 14 }} />}      label="Contact Person" value={client.primary_contact} />
        <InfoRow icon={<BusinessIcon sx={{ fontSize: 14 }} />}    label="Designation"    value={client.contact_title} />
        <InfoRow icon={<EmailIcon sx={{ fontSize: 14 }} />}       label="Email"          value={client.email} />
        <InfoRow icon={<PhoneIcon sx={{ fontSize: 14 }} />}       label="Phone"          value={client.phone} />
        <InfoRow icon={<LanguageIcon sx={{ fontSize: 14 }} />}    label="Website"        value={client.website} />
        <InfoRow icon={<LocationOnIcon sx={{ fontSize: 14 }} />}  label="Location"       value={[client.city, client.state, client.country].filter(Boolean).join(", ")} />

        <Typography sx={{ ...OVERLINE_SX, color: COLORS.muted, mb: 1, mt: 2 }}>Commercial</Typography>
        <InfoRow icon={<WorkIcon sx={{ fontSize: 14 }} />}        label="Company Size"   value={client.company_size} />
        <InfoRow icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}  label="Billing Rate"   value={client.billing_rate ? `${client.billing_rate}%` : null} />
        <InfoRow icon={<EmojiEventsIcon sx={{ fontSize: 14 }} />} label="Payment Terms"  value={client.payment_terms} />
        <InfoRow icon={<TimelineIcon sx={{ fontSize: 14 }} />}    label="Contract Start" value={client.agreement_start ? new Date(client.agreement_start).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null} />
        <InfoRow icon={<TimelineIcon sx={{ fontSize: 14 }} />}    label="Contract End"   value={client.agreement_end   ? new Date(client.agreement_end).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null} />

        {client.notes && (
          <Box mt={2} p={2} sx={{ bgcolor: "#F8F4FF", borderRadius: `${RADIUS.sm}px`, border: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ ...OVERLINE_SX, color: COLORS.muted, mb: 0.5 }}>Notes</Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.ink, lineHeight: 1.6, overflowWrap: "anywhere" }}>{client.notes}</Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1} mt={2} pt={2} sx={{ borderTop: "1px solid #F0EAF9" }}>
          <Button variant="outlined" size="small" startIcon={<WorkOutlineIcon sx={{ fontSize: 14 }} />} onClick={() => onViewJobs(client)}
            sx={{ ...BTN_SX, flex: 1, minWidth: 0, height: CONTROL_HEIGHT.md, borderColor: COLORS.border, color: "#4F378B", "&:hover": { borderColor: "#4F378B", bgcolor: "#F3EEF9" } }}>
            Jobs
          </Button>
          <Button variant="outlined" size="small" startIcon={<PersonSearchIcon sx={{ fontSize: 14 }} />} onClick={() => onViewCandidates(client)}
            sx={{ ...BTN_SX, flex: 1, minWidth: 0, height: CONTROL_HEIGHT.md, borderColor: COLORS.border, color: "#1B6B3A", "&:hover": { borderColor: "#1B6B3A", bgcolor: "#D4FAE0" } }}>
            Candidates
          </Button>
          <Button variant="contained" size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => onEdit(client)}
            sx={{ ...BTN_SX, height: CONTROL_HEIGHT.md, flexShrink: 0, background: "linear-gradient(135deg, #6750A4, #4F378B)", "&:hover": { background: "linear-gradient(135deg, #4F378B, #2D1B5E)" }, boxShadow: "0 2px 8px rgba(79,55,139,0.25)" }}>
            Edit
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Add/Edit Form Dialog ─────────────────────────────────────────────────────
function ClientFormDialog({ open, onClose, formTarget, formData, onChange, onSave, saving, formError, onClearError }) {
  const handleClose = () => { if (!saving) onClose(); };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
      <DialogTitle component="div" sx={{ borderBottom: "1px solid #F0EAF9", px: 3, py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box minWidth={0}>
            <Typography component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.3, color: COLORS.primaryDeep }}>
              {formTarget ? "Edit Client" : "Add New Client"}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5 }}>
              {formTarget ? `Editing ${formTarget.company_name}` : "Fill in the details to create a new client account"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={saving} sx={{ color: COLORS.faint, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={onSave}>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: `${RADIUS.sm}px` }} onClose={onClearError}>
              {formError}
            </Alert>
          )}
          {/* Basic */}
          <Box sx={{ borderLeft: "3px solid #6750A4", pl: 1.5, mb: 2 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, fontWeight: 700, color: "#4F378B" }}>Basic Information</Typography>
          </Box>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Client ID"     name="client_id"     value={formData.client_id}     onChange={onChange} disabled={!!formTarget} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Company Name"  name="company_name"  value={formData.company_name}  onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select size="small" required label="Industry" name="industry" value={formData.industry} onChange={onChange} fullWidth sx={FIELD_SX}>
                {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select size="small" label="Status" name="relationship_status" value={formData.relationship_status} onChange={onChange} fullWidth sx={FIELD_SX}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Company Size" name="company_size" value={formData.company_size} onChange={onChange} placeholder="e.g. 100–500" fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" label="Location" name="location" value={formData.location} onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
          </Grid>

          {/* Contact */}
          <Box sx={{ borderLeft: "3px solid #2E9B54", pl: 1.5, mb: 2 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, fontWeight: 700, color: "#1B6B3A" }}>Contact Information</Typography>
          </Box>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Primary Contact" name="primary_contact" value={formData.primary_contact} onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Title"           name="contact_title"   value={formData.contact_title}   onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" required type="email" label="Email" name="email" value={formData.email} onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" required label="Phone" name="phone" value={formData.phone} onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" label="City"    name="city"    value={formData.city}    onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" label="State"   name="state"   value={formData.state}   onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" label="Country" name="country" value={formData.country} onChange={onChange} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}><TextField size="small" label="Website" name="website" value={formData.website} onChange={onChange} placeholder="https://…" fullWidth sx={FIELD_SX} /></Grid>
          </Grid>

          {/* Billing */}
          <Box sx={{ borderLeft: "3px solid #F59E0B", pl: 1.5, mb: 2 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, fontWeight: 700, color: "#7A5200" }}>Billing & Agreement</Typography>
          </Box>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}><TextField size="small" type="number" label="Billing Rate (%)" name="billing_rate" value={formData.billing_rate} onChange={onChange} inputProps={{ step: "0.01" }} fullWidth sx={FIELD_SX} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select size="small" label="Payment Terms" name="payment_terms" value={formData.payment_terms} onChange={onChange} fullWidth sx={FIELD_SX}>
                {PAYMENT_TERMS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" type="date" label="Contract Start" name="agreement_start"
                value={formData.agreement_start ? String(formData.agreement_start).split("T")[0] : ""}
                onChange={onChange} InputLabelProps={{ shrink: true }} fullWidth sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" type="date" label="Contract End" name="agreement_end"
                value={formData.agreement_end ? String(formData.agreement_end).split("T")[0] : ""}
                onChange={onChange} InputLabelProps={{ shrink: true }} fullWidth sx={FIELD_SX} />
            </Grid>
          </Grid>
          <TextField multiline rows={3} size="small" label="Notes" name="notes" value={formData.notes} onChange={onChange} fullWidth sx={FIELD_SX} />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F0EAF9", justifyContent: "flex-end", gap: 1.5, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button onClick={handleClose} disabled={saving} sx={{ ...BTN_SX, height: CONTROL_HEIGHT.md, px: 2, color: COLORS.muted }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}
            sx={{ background: "linear-gradient(135deg, #6750A4, #4F378B)", "&:hover": { background: "linear-gradient(135deg, #4F378B, #2D1B5E)" }, ...BTN_SX, height: CONTROL_HEIGHT.md, px: 3, boxShadow: "0 2px 8px rgba(79,55,139,0.3)" }}>
            {saving && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
            {formTarget ? "Update Client" : "Create Client"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
// export default function ClientManagement() {
export default function ClientManagement({ onViewJobs: onViewJobsProp, onViewCandidates: onViewCandidatesProp } = {}) {

  const navigate = useNavigate();
  // Standalone /clients route pads itself; embedded in a dashboard tab the host provides the padding.
  const isStandalone = useLocation().pathname.replace(/\/+$/, "") === "/clients";

  const CLIENT_URL  = process.env.REACT_APP_API_CLIENTS_URL;
  const JOBS_URL    = process.env.REACT_APP_API_JOBS_URL;
  // const RESUMES_URL = process.env.REACT_APP_API_RESUMES_URL;

  const [clients,      setClients]      = useState([]);
  const [jobs,         setJobs]         = useState([]);
  // const [resumes,      setResumes]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusF,      setStatusF]      = useState("");
  const [industryF,    setIndustryF]    = useState("");
  const [selected,     setSelected]     = useState(null);
  const [formOpen,     setFormOpen]     = useState(false);
  const [deleteOpen,   setDeleteOpen]   = useState(false);
  const [formTarget,   setFormTarget]   = useState(null);
  const [formData,     setFormData]     = useState(EMPTY_FORM);
  const [saving,       setSaving]       = useState(false);
  const [analyticsMap, setAnalyticsMap] = useState({});
  const [formError, setFormError] = useState("");
  const [deleting,     setDeleting]     = useState(false);
  const [deleteError,  setDeleteError]  = useState("");
  const [loadError,    setLoadError]    = useState("");
  const [notice,       setNotice]       = useState("");
  const savingRef   = useRef(false);
  const deletingRef = useRef(false);
  const mountedRef  = useRef(true);
  const loadSeqRef  = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── loaders ─────────────────────────────────────────────────────────────────
  // `silent` refreshes keep the current list on screen (no full-page spinner
  // that would unmount the dialogs and flash the empty state).
  const loadClients = useCallback(async ({ silent = false } = {}) => {
    const seq = ++loadSeqRef.current;
    try {
      if (!silent) setLoading(true);
      setLoadError("");
      if (!CLIENT_URL) throw new Error("API URL is not configured. Check your .env file.");
      const data = await apiFetch(`${CLIENT_URL}/`);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      setClients(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      setLoadError(err?.message || "Failed to load clients");
      if (!silent) setClients([]);
    } finally {
      if (mountedRef.current && seq === loadSeqRef.current) setLoading(false);
    }
  }, [CLIENT_URL]);

  const loadJobs = useCallback(async () => {
    if (!JOBS_URL) return;
    try {
      const data = await apiFetch(`${JOBS_URL}/`);
      if (mountedRef.current) setJobs(Array.isArray(data?.data) ? data.data : []);
    } catch { if (mountedRef.current) setJobs([]); }
  }, [JOBS_URL]);

  // const loadResumes = useCallback(async () => {
  //   if (!RESUMES_URL) return;
  //   try { const data = await apiFetch(`${RESUMES_URL}/`); setResumes(data.data || []); } catch { setResumes([]); }
  // }, [RESUMES_URL]);

  const loadAnalytics = useCallback(async () => {
    if (!CLIENT_URL) return;
    try {
      const data = await apiFetch(`${CLIENT_URL}/analytics/all`);
      const map = {};
      (data?.data?.clients || []).forEach(c => {
        if (!c) return;
        if (c.client_id != null)     map[c.client_id]     = c;
        if (c.client_ref_id != null) map[c.client_ref_id] = c;
      });
      if (mountedRef.current) setAnalyticsMap(map);
    } catch {
      if (mountedRef.current) setAnalyticsMap({});
    }
  }, [CLIENT_URL]);

  useEffect(() => {
    loadClients();
    loadJobs();
    // loadResumes();
    loadAnalytics();
  }, [loadClients, loadJobs,  loadAnalytics]);

  const refreshAll = useCallback(() => {
    loadClients({ silent: true });
    loadJobs();
    loadAnalytics();
  }, [loadClients, loadJobs, loadAnalytics]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  // ── count helpers ────────────────────────────────────────────────────────────
const clientJobs     = (c) => jobs.filter(j => j.client_id === c.client_id || j.client_id === c._id);
const analyticsFor   = (c) => analyticsMap[String(c._id)] || analyticsMap[c.client_id];
const jobCount       = (c) => analyticsFor(c)?.total_jds        ?? clientJobs(c).length;
const candidateCount = (c) => analyticsFor(c)?.total_candidates ?? 0;
const placedCount    = (c) => analyticsFor(c)?.total_hired      ?? 0;
  // analyticsMap holds each entry under two keys — dedupe before summing.
  const allAnalytics = Array.from(new Set(Object.values(analyticsMap))).filter(a => a?.client_id);
  const stats = {
    total:      clients.length,
    active:     clients.filter(c => c.relationship_status === "Active").length,
    activeJobs: allAnalytics.length > 0 ? allAnalytics.reduce((s, a) => s + (a.total_jds   || 0), 0) : jobs.length,
    placements: allAnalytics.reduce((s, a) => s + (a.total_hired || 0), 0),
  };

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    const has = (v) => v != null && String(v).toLowerCase().includes(q);
    return (
      (!q        || has(c.company_name) || has(c.primary_contact) || has(c.client_id))
      && (!statusF   || c.relationship_status === statusF)
      && (!industryF || c.industry === industryF)
    );
  });
const handleViewJobs = (cl) => {
  if (onViewJobsProp) onViewJobsProp(cl);
  else navigate(`/candidate-browser?company=${encodeURIComponent(cl?.company_name || "")}`);
};
const handleViewCandidates = (cl) => {
  if (onViewCandidatesProp) onViewCandidatesProp(cl);
  else navigate(`/candidate-browser?company=${encodeURIComponent(cl?.company_name || "")}`);
};
  // ── CRUD ─────────────────────────────────────────────────────────────────────
  // Normalise null/undefined server fields to "" so inputs stay controlled.
  const toFormData = (c) => {
    const out = { ...EMPTY_FORM };
    Object.keys(EMPTY_FORM).forEach(k => { if (c?.[k] != null) out[k] = c[k]; });
    return out;
  };
  const openCreate = () => { if (saving) return; setFormTarget(null); setFormData(EMPTY_FORM); setFormError(""); setFormOpen(true); };
  const openEdit   = (c) => { if (saving) return; setFormTarget(c); setFormData(toFormData(c)); setFormError(""); setFormOpen(true); };
  const openDelete = (c) => { if (deleting) return; setFormTarget(c); setDeleteError(""); setDeleteOpen(true); };
  const closeDelete = () => { if (!deleting) setDeleteOpen(false); };

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    const validationMsg = validateClient(formData);
    if (validationMsg) { setFormError(validationMsg); return; }

    const payload = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
    );
    if (payload.website && !/^https?:\/\//i.test(payload.website)) payload.website = `https://${payload.website}`;

    savingRef.current = true; setSaving(true); setFormError("");
    const isEdit = !!formTarget;
    try {
      if (isEdit) {
        if (!formTarget?._id) throw new Error("Cannot update: client id is missing.");
        await apiFetch(`${CLIENT_URL}/${encodeURIComponent(formTarget._id)}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        await apiFetch(`${CLIENT_URL}/`, { method: "POST", body: JSON.stringify(payload) });
      }
      if (!mountedRef.current) return;
      setFormOpen(false);
      if (isEdit && selected?._id === formTarget._id) setSelected(null);
      setNotice(isEdit ? "Client updated successfully." : "Client created successfully.");
      refreshAll();
    } catch (err) {
      if (mountedRef.current) setFormError(err?.message || "Save failed");
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deletingRef.current) return;
    const target = formTarget;
    if (!target?._id) { setDeleteError("Cannot delete: client id is missing."); return; }
    deletingRef.current = true; setDeleting(true); setDeleteError("");
    try {
      await apiFetch(`${CLIENT_URL}/${encodeURIComponent(target._id)}`, { method: "DELETE" });
      if (!mountedRef.current) return;
      setDeleteOpen(false);
      if (selected?._id === target._id) setSelected(null);
      setNotice(`${target.company_name || "Client"} deleted.`);
      refreshAll();
    } catch (err) {
      if (mountedRef.current) setDeleteError(err?.message || "Delete failed");
    } finally {
      deletingRef.current = false;
      if (mountedRef.current) setDeleting(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  const pagePad = isStandalone ? `${PAGE_PADDING}px` : 0;

  if (loading && clients.length === 0) return (
    <ThemeProvider theme={recruiterTheme}>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 5 }}>
        <CircularProgress sx={{ color: "#6750A4" }} />
      </Box>
    </ThemeProvider>
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
    <Box sx={{ p: pagePad, fontFamily: FONT_BODY }}>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}
          action={
            <Button color="inherit" size="small" disabled={loading} onClick={() => { loadClients({ silent: clients.length > 0 }); loadJobs(); loadAnalytics(); }}
              sx={{ ...BTN_SX }}>
              {loading ? "Retrying…" : "Retry"}
            </Button>
          }>
          {loadError}
        </Alert>
      )}
      {notice && <Alert severity="success" onClose={() => setNotice("")} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>{notice}</Alert>}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box minWidth={0}>
          <PageTitle>Client Management</PageTitle>
          <PageSubtitle>
            {clients.length} client{clients.length !== 1 ? "s" : ""} · {stats.active} active · {stats.activeJobs} open jobs
          </PageSubtitle>
        </Box>
        <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add New Client
        </PrimaryButton>
      </Box>

      {/* KPI strip */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2, mb: 3 }}>
        <KPICard label="Total Clients"    value={stats.total}      icon={<BusinessIcon sx={{ fontSize: 20 }} />}    accent="#4F378B" sub="All time" />
        <KPICard label="Active Clients"   value={stats.active}     icon={<CheckCircleIcon sx={{ fontSize: 20 }} />} accent="#2E9B54" sub="Currently engaged"
          trend={stats.total > 0 ? `${Math.round((stats.active / stats.total) * 100)}%` : null} />
        <KPICard label="Open Jobs"        value={stats.activeJobs} icon={<WorkIcon sx={{ fontSize: 20 }} />}        accent="#0ea5e9" sub="Across all clients" />
        <KPICard label="Total Placements" value={stats.placements} icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}  accent="#F59E0B" sub='Candidates "Hired"' />
      </Box>

      {/* Filters */}
      {clients.length > 0 && (
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={3}
          sx={{ p: 2, bgcolor: "#fff", borderRadius: `${RADIUS.lg}px`, border: `1px solid ${COLORS.border}`, boxShadow: SHADOW.card }}>
          <FilterListIcon sx={{ fontSize: 20, color: COLORS.faint }} />
          <TextField
            placeholder="Search clients, contacts, IDs…"
            value={search} onChange={e => setSearch(e.target.value)} size="small"
            sx={{ ...FIELD_SX, flexGrow: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "#B0A0D0" }} /></InputAdornment> }}
          />
          <TextField select value={statusF} onChange={e => setStatusF(e.target.value)} size="small" label="Status"
            sx={{ ...FIELD_SX, minWidth: 160 }}>
            <MenuItem value="">All Statuses</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select value={industryF} onChange={e => setIndustryF(e.target.value)} size="small" label="Industry"
            sx={{ ...FIELD_SX, minWidth: 200 }}>
            <MenuItem value="">All Industries</MenuItem>
            {INDUSTRIES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </TextField>
          {(search || statusF || industryF) && (
            <Box onClick={() => { setSearch(""); setStatusF(""); setIndustryF(""); }}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, height: 24, bgcolor: COLORS.border, borderRadius: `${RADIUS.pill}px`, cursor: "pointer", whiteSpace: "nowrap" }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: "#4F378B", fontVariantNumeric: "tabular-nums" }}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
              </Typography>
              <CloseIcon sx={{ fontSize: 12, color: "#4F378B" }} />
            </Box>
          )}
        </Box>
      )}

      {/* Empty states */}
      {clients.length === 0 && loadError ? null : clients.length === 0 ? (
        <Box sx={{ bgcolor: "#fff", borderRadius: `${RADIUS.lg}px`, border: `1px solid ${COLORS.border}`, boxShadow: SHADOW.card, py: 5, px: 3, textAlign: "center" }}>
          <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#EDE7F6", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <StorefrontIcon sx={{ fontSize: 36, color: "#B0A0D0" }} />
          </Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.primaryDeep, mb: 1 }}>No clients yet</Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.muted, mb: 3 }}>Add your first client to start managing engagements</Typography>
          <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>Add New Client</PrimaryButton>
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ bgcolor: "#fff", borderRadius: `${RADIUS.lg}px`, border: `1px solid ${COLORS.border}`, boxShadow: SHADOW.card, py: 5, px: 3, textAlign: "center" }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.muted }}>No clients match your filters.</Typography>
          <Box component="span" onClick={() => { setSearch(""); setStatusF(""); setIndustryF(""); }}
            sx={{ fontFamily: FONT_BODY, fontSize: 13, color: "#6750A4", cursor: "pointer", mt: 1, display: "block", fontWeight: 600 }}>
            Clear all filters
          </Box>
        </Box>
      ) : (
        /* Cards + Detail Panel */
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={selected ? 8 : 12}>
            <Grid container spacing={2}>
              {filtered.map(c => (
                <Grid item xs={12} sm={6} md={selected ? 6 : 4} key={c._id} sx={{ display: "flex" }}>
                  <ClientCard
                    client={c}
                    jobCnt={jobCount(c)} candidateCnt={candidateCount(c)} placedCnt={placedCount(c)}
                    isSelected={selected?._id === c._id}
                    onSelect={(cl) => setSelected(prev => prev?._id === cl._id ? null : cl)}
                    onEdit={openEdit}
                    onDelete={openDelete}
                    onViewJobs={handleViewJobs}
                    onViewCandidates={handleViewCandidates}
                  />
                </Grid>
              ))}
            </Grid>
            <Box mt={2}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.muted }}>
                Showing <strong style={{ color: "#3D2B6B" }}>{filtered.length}</strong> of <strong style={{ color: "#3D2B6B" }}>{clients.length}</strong> clients
                {selected && <span style={{ marginLeft: 12, color: "#6750A4", fontWeight: 600 }}>· {selected.company_name} selected — click again to close</span>}
              </Typography>
            </Box>
          </Grid>

          {selected && (
            <Grid item xs={12} md={4}>
              <DetailPanel
                client={selected}
                jobCnt={jobCount(selected)} candidateCnt={candidateCount(selected)} placedCnt={placedCount(selected)}
                onClose={() => setSelected(null)}
                onEdit={(c) => { openEdit(c); setSelected(null); }}
                onViewJobs={handleViewJobs}
                onViewCandidates={handleViewCandidates}
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Dialogs */}
      <ClientFormDialog
        open={formOpen} onClose={() => setFormOpen(false)}
        formTarget={formTarget} formData={formData}
        onChange={handleChange} onSave={handleSave} saving={saving}
        formError={formError} onClearError={() => setFormError("")}
      />

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
        <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, color: COLORS.primaryDeep, px: 3, pt: 3, pb: 1 }}>Delete Client</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }} onClose={() => setDeleteError("")}>{deleteError}</Alert>
          )}
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.ink, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{formTarget?.company_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: "flex-end", gap: 1.5, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button onClick={closeDelete} disabled={deleting} sx={{ ...BTN_SX, height: CONTROL_HEIGHT.md, px: 2, color: COLORS.muted }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            sx={{ ...BTN_SX, height: CONTROL_HEIGHT.md, px: 3 }}>
            {deleting && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
}